import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { ImpersonationService } from 'src/app/services/impersonation/impersonation.service';
import { LoadUserAction, LogoutAction } from 'src/app/store/user/user.actions';
import { UserState } from 'src/app/store/user/user.state';

// AuthGuard checks wether a user has a valid session and at least one membership
export const AuthGuard = async (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const kratos = inject(KratosAuthenticationService);
  const impersonation = inject(ImpersonationService);

  try {
    let user = store.selectSnapshot(UserState.getUser);

    // when user is empty we probably did not fetch it yet as the user just logged in -> fetch
    if (!user) {
      await firstValueFrom(store.dispatch(new LoadUserAction()));
      user = store.selectSnapshot(UserState.getUser);
    }

    if (!user) {
      throw new Error('No user');
    }

    // validate session; while impersonating, the Kratos session in this
    // browser is the staff member's, not the user's
    let session = store.selectSnapshot(UserState.getSession);
    if (
      !impersonation.active() &&
      (!session || Date.now() > session?.expiresAt.getTime())
    ) {
      // Session missing or stale in store
      // Try to recover before logout
      try {
        await kratos.setSessionFromWhoami();
        session = store.selectSnapshot(UserState.getSession);
      } catch {
        // session is invalid
      }

      if (!session || Date.now() > session?.expiresAt.getTime()) {
        store.dispatch(new LogoutAction());
        throw new Error('Session expired');
      }
    }

    // no organization yet: onboarding creates the first one (and is itself
    // guarded, so it must be exempt or the redirect would loop)
    if (user.memberships.length == 0 && !state.url.startsWith('/onboarding')) {
      return router.createUrlTree(['/onboarding']);
    }

    return true;
  } catch (ex) {
    console.warn(ex);
    // The login page sends a valid session straight back here; when the app
    // could not load for it (API down, no user) that would loop. `appFailed`
    // makes it ask instead (try again, or another account), and the session
    // survives a passing outage.
    router.navigate(['/login'], { state: { appFailed: true } });
  }

  return true;
};
